import { PlusOutlined } from '@ant-design/icons';
import { designTokens } from '@app/theme';
import { usePermissions } from '@refinedev/core';
import { Permissions } from '@shared/constants/permissions';
import { formatDateTime, formatRelative } from '@shared/lib/format-date';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Button,
  Card,
  Empty,
  Grid,
  Pagination,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  notification,
} from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { fetchAdmins } from './api';
import { AddAdminDrawer } from './components/AddAdminDrawer';

const { Title, Text } = Typography;

const PAGE_SIZE = 20;
export const ADMINS_KEY = ['admin-users', 'admins'] as const;

/**
 * Раздел «Администраторы»: пользователи, у которых есть роль с правом `admin.access`.
 *
 * Список — `GET /api/admin/users?adminOnly=true` напрямую через SDK + react-query
 * (Refine data-provider для `users` не умеет фильтры). Карточка каждого ведёт
 * на общую страницу пользователя `/users/:id`, где меняются статус и полный набор ролей.
 *
 * Кнопка «Добавить администратора» требует сразу `users.manage` (создание пользователя)
 * и `users.roles.write` (назначение ролей) — так же гейтит бэк; плюс `roles.read`,
 * без которого не загрузить справочник ролей.
 */
export function AdminsListPage() {
  const { t } = useTranslation(['admins', 'common']);
  const screens = Grid.useBreakpoint();
  const qc = useQueryClient();
  const { data: perms } = usePermissions<string[]>({});
  const canAdd =
    (perms?.includes(Permissions.USERS_MANAGE) ?? false) &&
    (perms?.includes(Permissions.USERS_ROLES_WRITE) ?? false) &&
    (perms?.includes(Permissions.ROLES_READ) ?? false);

  const [currentPage, setCurrentPage] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const adminsQuery = useQuery({
    queryKey: [...ADMINS_KEY, currentPage],
    queryFn: () => fetchAdmins(currentPage, PAGE_SIZE),
  });

  const admins = adminsQuery.data?.items ?? [];
  const total = adminsQuery.data?.total ?? 0;

  return (
    <Space direction="vertical" size={24} style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div>
          <Title level={3} style={{ marginBlockEnd: 4, fontWeight: 600 }}>
            {t('admins:title')}
          </Title>
          <Text type="secondary" style={{ fontSize: 14 }}>
            {t('admins:subtitle')}
          </Text>
        </div>
        {canAdd && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setDrawerOpen(true);
            }}
          >
            {t('admins:add')}
          </Button>
        )}
      </div>

      <Card style={{ borderColor: designTokens.slate200 }} styles={{ body: { padding: 0 } }}>
        <Spin spinning={adminsQuery.isLoading}>
          {!adminsQuery.isLoading && admins.length === 0 ? (
            <div style={{ padding: 32 }}>
              <Empty description={t('admins:empty')} />
            </div>
          ) : (
            admins.map((user, idx) => (
              <div
                key={user.id}
                style={{
                  padding: '12px 16px',
                  borderBlockStart: idx > 0 ? `1px solid ${designTokens.slate100}` : 'none',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <Link
                    to={`/users/${user.id ?? ''}`}
                    style={{
                      fontFamily: '"JetBrains Mono", monospace',
                      fontVariantNumeric: 'tabular-nums',
                      fontSize: 14,
                      fontWeight: 500,
                      color: designTokens.navy900,
                      borderBottom: `1px dashed ${designTokens.slate300}`,
                    }}
                  >
                    {user.phone}
                  </Link>
                  <Tag
                    color={user.enabled ? 'green' : 'red'}
                    style={{ fontSize: 11, fontWeight: 600, marginInlineEnd: 0 }}
                  >
                    {user.enabled ? t('admins:status.enabled') : t('admins:status.disabled')}
                  </Tag>
                </div>

                <div style={{ marginBlockStart: 6 }}>
                  <Space wrap size={4}>
                    {(user.roles ?? []).map((role) => (
                      <Tag
                        key={role.id}
                        style={{ fontSize: 11, fontWeight: 600, letterSpacing: 0.3, margin: 0 }}
                      >
                        {role.name}
                      </Tag>
                    ))}
                  </Space>
                </div>

                <div
                  style={{
                    marginBlockStart: 6,
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '4px 16px',
                  }}
                >
                  <Tooltip title={user.lastLoginAt ? formatDateTime(user.lastLoginAt) : undefined}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('admins:columns.lastLoginAt')}:{' '}
                      {user.lastLoginAt ? formatRelative(user.lastLoginAt) : t('admins:never')}
                    </Text>
                  </Tooltip>
                  <Tooltip title={formatDateTime(user.createdAt)}>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {t('admins:columns.createdAt')}: {formatRelative(user.createdAt)}
                    </Text>
                  </Tooltip>
                </div>
              </div>
            ))
          )}

          {total > PAGE_SIZE && (
            <div
              style={{
                padding: '12px 16px',
                borderBlockStart: `1px solid ${designTokens.slate100}`,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Pagination
                current={currentPage}
                pageSize={PAGE_SIZE}
                total={total}
                onChange={(p) => {
                  setCurrentPage(p);
                }}
                simple={!screens.md}
                showSizeChanger={false}
                showTotal={
                  screens.md
                    ? (t2, range) =>
                        t('admins:pagination.total', { from: range[0], to: range[1], total: t2 })
                    : undefined
                }
              />
            </div>
          )}
        </Spin>
      </Card>

      <AddAdminDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
        }}
        onDone={({ mode, user }) => {
          setDrawerOpen(false);
          void qc.invalidateQueries({ queryKey: ADMINS_KEY });
          void qc.invalidateQueries({ queryKey: ['admin-users'] });
          notification.success({
            message: t(mode === 'created' ? 'admins:drawer.created' : 'admins:drawer.attached', {
              phone: user.phone,
            }),
            placement: 'topRight',
          });
        }}
      />
    </Space>
  );
}
